import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaArrowRight, FaQuestionCircle } from 'react-icons/fa';
import { FaUserCheck, FaAward } from 'react-icons/fa';
import Seo from '../../components/Seo/Seo';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqCategories = [
    {
      category: 'General Questions',
      questions: [
        {
          q: 'What areas in Brisbane do you service?',
          a: 'We service all suburbs listed on our Location page across Brisbane. If you\'re unsure whether we cover your area, please contact us and we\'ll confirm.',
        },
        {
          q: 'Are you fully insured?',
          a: 'Yes, The Skilled Group is fully insured with comprehensive public liability insurance. This means you\'re protected in the unlikely event of any damage during our cleaning service. We also carry workers\' compensation insurance for all our staff.',
        },
        {
          q: 'Do I need to be home during the cleaning?',
          a: 'It\'s not required, but we recommend being present for the first appointment so you can discuss your specific requirements with our team. For recurring cleans, many of our customers provide access instructions and are not present during the service.',
        },
        {
          q: 'What cleaning products do you use?',
          a: 'We use professional-grade, eco-friendly cleaning products that are safe for children, pets, and the environment. Our products are non-toxic, biodegradable, and effective against dirt, stains, bacteria, and allergens.',
        },
      ],
    },
    {
      category: 'Booking & Scheduling',
      questions: [
        {
          q: 'How do I book a cleaning service?',
          a: 'You can book a cleaning service through our website by visiting the Booking page and filling out the form. Alternatively, you can call us at 0481577831 or email us at info@theskilledgroup.com. We\'ll confirm your booking within 2 hours.',
        },
        {
          q: 'Can I book a same-day service?',
          a: 'Yes, we offer same-day service subject to availability. For the best chance of securing a same-day appointment, we recommend calling us directly. Same-day bookings are available for most of our cleaning services.',
        },
        {
          q: 'How far in advance should I book?',
          a: 'We recommend booking at least 24–48 hours in advance to secure your preferred date and time. However, we understand that cleaning needs can arise unexpectedly, so we do our best to accommodate last-minute requests.',
        },
        {
          q: 'Can I reschedule or cancel my booking?',
          a: 'Yes, you can reschedule or cancel your booking free of charge with at least 24 hours\' notice. Please contact us by phone or email to make any changes to your appointment.',
        },
      ],
    },
    {
      category: 'Pricing & Payment',
      questions: [
        {
          q: 'How much do your cleaning services cost?',
          a: 'Our pricing varies depending on the type of service, property size, and specific requirements. We provide free, no-obligation quotes so you know the exact cost before booking. Our prices are competitive and transparent with no hidden fees.',
        },
        {
          q: 'Do you offer free quotes?',
          a: 'Yes! We provide free, no-obligation quotes for all our services. You can request a quote through our website, by phone, or by email. We\'ll assess your needs and provide a detailed quote within 24 hours.',
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept cash, bank transfer, credit cards (Visa, MasterCard, American Express), and EFTPOS. Payment is typically due upon completion of the service unless otherwise arranged.',
        },
        {
          q: 'Are there any hidden fees?',
          a: 'Absolutely not. The quote we provide is the price you pay. There are no hidden fees, call-out charges, or surprise costs. If additional work is needed beyond the original scope, we\'ll discuss it with you and get approval before proceeding.',
        },
      ],
    },
    {
      category: 'Services',
      questions: [
        {
          q: 'What is included in pest control?',
          a: 'Our pest control service includes an inspection, targeted treatment for common pests (such as ants, cockroaches, spiders, and rodents), and practical prevention advice. Scope can vary based on property type and infestation level, and we explain this clearly before treatment.',
        },
        {
          q: 'Do you clean tile and grout in bathrooms and kitchens?',
          a: 'Yes. Our tile and grout cleaning service is designed for bathrooms, kitchens, laundries, and other tiled areas. We use professional equipment to remove built-up grime and help restore grout appearance.',
        },
        {
          q: 'How long does carpet cleaning take?',
          a: 'The duration depends on the size of the area and the level of soiling. As a general guide, a standard 3-bedroom home takes approximately 1–2 hours. Heavily soiled carpets may take longer. Drying time is typically 2–4 hours after cleaning.',
        },
      ],
    },
    {
      category: 'After the Service',
      questions: [
        {
          q: 'What if I\'m not satisfied with the cleaning?',
          a: 'Your satisfaction is guaranteed. If you\'re not happy with any aspect of our service, please contact us within 24 hours and we\'ll return to re-clean the affected areas free of charge. We take pride in our work and want every customer to be completely satisfied.',
        },
        {
          q: 'How long does it take for carpets to dry after steam cleaning?',
          a: 'Carpets typically take 2–4 hours to dry after steam cleaning, depending on ventilation, humidity, and carpet thickness. We recommend keeping windows open and using fans to speed up the drying process. Avoid walking on damp carpets with shoes.',
        },
        {
          q: 'Do you provide an invoice or receipt?',
          a: 'Yes, we provide a detailed invoice or receipt for every service so you have a complete record of the work completed.',
        },
      ],
    },
  ];

  // Flatten for indexing
  let globalIndex = 0;

  return (
    <div>
      <Seo
        title="FAQ"
        description="Find answers to common questions about bookings, pricing, service areas, and cleaning services from The Skilled Group."
        canonicalPath="/faq"
      />
      {/* Page Header */}
      <div className="page-header">
        <div className="container-custom">
          <h1>Frequently Asked Questions</h1>
          <p>
            Find answers to common questions about our cleaning services,
            booking process, and more.
          </p>
        </div>
      </div>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto mb-14">
            <div className="text-center mb-10 md:mb-12">
              <span className="inline-block bg-primary-50 text-primary-500 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                Why The Skilled Group
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-800 mb-4">
                Why Choose The Skilled Group?
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-lg">
                With years of experience serving Brisbane households and businesses, here's
                why thousands of customers trust The Skilled Group for their cleaning needs.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div className="bg-gray-50 rounded-xl p-6 md:p-8 border border-gray-100">
                <div className="w-14 h-14 bg-primary-50 rounded-xl flex items-center justify-center mb-5">
                  <FaUserCheck className="text-2xl text-primary-500" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-gray-800 mb-3">
                  Experienced Professionals
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Our team consists of trained, vetted, and experienced cleaning professionals who take pride in their work.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 md:p-8 border border-gray-100">
                <div className="w-14 h-14 bg-primary-50 rounded-xl flex items-center justify-center mb-5">
                  <FaAward className="text-2xl text-primary-500" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-gray-800 mb-3">
                  Satisfaction Guaranteed
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  We stand behind our work with a 100% satisfaction guarantee. If you're not happy, we'll re-clean for free.
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            {faqCategories.map((cat) => (
              <div key={cat.category} className="mb-10 last:mb-0">
                {/* Category heading */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FaQuestionCircle className="text-primary-500" />
                  </div>
                  <h2 className="text-xl font-heading font-bold text-gray-800">
                    {cat.category}
                  </h2>
                </div>

                {/* Questions */}
                <div className="space-y-3">
                  {cat.questions.map((item) => {
                    const currentIndex = globalIndex++;
                    const isOpen = openIndex === currentIndex;

                    return (
                      <div
                        key={currentIndex}
                        className={`border rounded-xl transition-all duration-200 ${
                          isOpen
                            ? 'border-primary-200 bg-primary-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <button
                          onClick={() => toggle(currentIndex)}
                          className="w-full flex items-center justify-between p-5 text-left"
                          aria-expanded={isOpen}
                        >
                          <span
                            className={`font-medium text-sm md:text-base pr-4 ${
                              isOpen ? 'text-primary-500' : 'text-gray-800'
                            }`}
                          >
                            {item.q}
                          </span>
                          <FaChevronDown
                            className={`text-sm flex-shrink-0 transition-transform duration-200 ${
                              isOpen
                                ? 'rotate-180 text-primary-500'
                                : 'text-gray-400'
                            }`}
                          />
                        </button>

                        {isOpen && (
                          <div className="px-5 pb-5">
                            <div className="border-t border-primary-100 pt-4">
                              <p className="text-gray-600 text-sm leading-relaxed">
                                {item.a}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Reset counter for re-render */}
            <span className="hidden">{(globalIndex = 0)}</span>
          </div>

          {/* CTA */}
          <div className="max-w-4xl mx-auto mt-16 text-center">
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12 border border-gray-100">
              <h3 className="text-2xl font-heading font-bold text-gray-800 mb-3">
                Still Have Questions?
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Can't find the answer you're looking for? Our friendly team is here
                to help.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  Contact Us
                  <FaArrowRight className="text-sm" />
                </Link>
                <a
                  href="tel:0481577831"
                  className="btn-outline inline-flex items-center justify-center gap-2"
                >
                  Call 0481577831
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;